import { useState } from 'react';
import { RevenueSplit } from './revenue.domain';
import {
  toRevenueSplitView,
  toDistributionView,
  toPayoutView,
  mapRawRevenueSplitToRevenueSplit,
  RevenueSplitView,
  DistributionView,
  PayoutView,
} from './revenueMappers';
import {
  RevenueModel,
  RevenueModelDispatcher,
  RevenueViewDispatcher,
  useRevenueModel,
} from './useRevenueModel';
import { Async } from '@client/new/helpers/types';
import _ from 'lodash';
import toast from 'react-hot-toast';
import auth from '@client/components/auth';

type RevenueViewProps = {
  influencerId: number;
};

type RevenueViewModel = {
  revenueSplitView: RevenueSplitView;
  distributionView: DistributionView;
  payoutView: PayoutView;
  showInfoAlert: boolean;
  hasPayoutPermission: boolean;
};
type GetDispatcherParams = {
  revenueSplit: Array<RevenueSplit>;
  asyncModel: Async<RevenueModel & { dispatch: RevenueModelDispatcher }>;
  cancelEdit: () => void;
  setIsEditMode: React.Dispatch<React.SetStateAction<boolean>>;
  updateSplitRule: (p: {
    value: number | undefined;
    productType: string;
  }) => void;
};
const getDispatcher =
  (p: GetDispatcherParams): RevenueViewDispatcher =>
  (action) => {
    if (p.asyncModel.type !== 'fetched') return;
    const model = p.asyncModel.value;

    switch (action.type) {
      case 'cancelRevenueSplitEdit':
        return p.cancelEdit();
      case 'saveRevenueSplit':
        if (p.revenueSplit[0].playbookPercent === undefined) {
          toast.error('Please enter playbook fee.');
          return;
        }
        p.setIsEditMode(false);
        return model.dispatch({
          type: 'saveRevenueSplit',
          payload: {
            referral: p.revenueSplit[0].referralPercent,
            playbook: p.revenueSplit[0].playbookPercent,
          },
        });
      case 'editRevenueSplit':
        return p.setIsEditMode(true);
      case 'updateSplitRule':
        return p.updateSplitRule(action.payload);
    }
  };

export const useRevenueView = ({
  influencerId,
}: RevenueViewProps): Async<RevenueViewModel> => {
  const asyncModel = useRevenueModel({ influencerId });
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [isRevenueSplitInitialized, setIsRevenueSplitInitialized] =
    useState<boolean>(false);
  const [revenueSplit, setRevenueSplit] = useState<Array<RevenueSplit>>([]);

  if (asyncModel.type !== 'fetched') return asyncModel;

  if (asyncModel.type === 'fetched' && !isRevenueSplitInitialized) {
    setIsRevenueSplitInitialized(true);
    setRevenueSplit(
      mapRawRevenueSplitToRevenueSplit(asyncModel.value.revenueSplit),
    );
  }

  const updateSplitRule = (p: {
    value: number | undefined;
    productType: string;
  }) => {
    setRevenueSplit((prev) =>
      prev.map((item) => {
        if (item.productType === p.productType) {
          return {
            ...item,
            playbookPercent: p.value,
            referralPercent: p.value !== undefined ? 100 - p.value : 0,
          };
        }
        return item;
      }),
    );
  };

  const cancelEdit = () => {
    setRevenueSplit(
      mapRawRevenueSplitToRevenueSplit(asyncModel.value.revenueSplit || []),
    );
    setIsEditMode(false);
  };

  const hasPayoutPermission = auth.hasPayoutsPermission();

  const dispatch = getDispatcher({
    revenueSplit,
    asyncModel,
    cancelEdit,
    setIsEditMode,
    updateSplitRule,
  });

  return {
    type: 'fetched',
    value: {
      revenueSplitView: toRevenueSplitView(
        revenueSplit,
        isEditMode,
        dispatch,
        hasPayoutPermission,
      ),
      distributionView: toDistributionView(asyncModel.value.distribution),
      payoutView: toPayoutView(asyncModel.value.payout),
      showInfoAlert: isEditMode,
      hasPayoutPermission,
    },
  };
};
